import React from 'react';
import { motion } from 'framer-motion';
import { ZapIcon, BookOpenIcon, HeadphonesIcon, CheckCircleIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
export function ProductReveal() {
  return <section className="bg-[#0A0A0A] py-20 md:py-28" data-id="element-195">
      <div className="max-w-6xl mx-auto px-6" data-id="element-196">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16" data-id="element-197">
          {/* Left — Intro */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6
        }} data-id="element-198">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F5F5F0] tracking-tight mb-4 leading-tight" data-id="element-199">
              Introducing CrownControl™
            </h2>
            <p className="text-xl text-[#C8A04A] font-medium mb-8" data-id="element-200">
              The Non-Hormonal 10-Minute Regrowth Protocol
            </p>
            <p className="text-lg text-[#9CA3AF] leading-relaxed" data-id="element-201">
              Everything you've read — the dormancy mechanism, the cellular
              energy lever, the validated wavelength, the non-hormonal pathway —
              is what CrownControl™ was engineered to deliver.
            </p>
            <p className="text-lg text-[#9CA3AF] leading-relaxed mt-4" data-id="element-202">
              It does one thing. It does it correctly. And it takes 10 minutes a
              day.
            </p>

            {/* Product image placeholder */}
            <div className="mt-10 rounded-lg border border-[#2A2A2A] overflow-hidden" data-id="element-203">
              <img src="https://cdn.magicpatterns.com/uploads/8YJt4PeSkc4Le95941HttS/ChatGPT_Image_Feb_17,_2026,_02_15_56_PM.png" alt="CrownControl Product and Results" className="w-full h-auto block" data-id="element-204" />
            </div>
          </motion.div>

          {/* Right — What You Get */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.15
        }} className="space-y-8" data-id="element-205">
            <h3 className="text-2xl font-bold text-[#F5F5F0] tracking-tight" data-id="element-206">
              What You Get
            </h3>

            {/* The Device */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-8" data-id="element-207">
              <div className="flex items-center gap-3 mb-5" data-id="element-208">
                <div className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center" data-id="element-209">
                  <ZapIcon className="w-5 h-5 text-[#C8A04A]" data-id="element-210" />
                </div>
                <h4 className="text-lg font-bold text-[#F5F5F0]" data-id="element-211">The Device</h4>
              </div>
              <ul className="space-y-3" data-id="element-212">
                <li className="flex items-start gap-3" data-id="element-213">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-214" />
                  <span className="text-[#9CA3AF]" data-id="element-215">
                    Laser diodes calibrated to the 650–670nm therapeutic window
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-216">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-217" />
                  <span className="text-[#9CA3AF]" data-id="element-218">
                    Power density matched to clinical trial specifications
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-219">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-220" />
                  <span className="text-[#9CA3AF]" data-id="element-221">
                    Full scalp coverage in a discreet, wearable hat format
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-222">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-223" />
                  <span className="text-[#9CA3AF]" data-id="element-224">
                    FDA-cleared device class for at-home use in androgenetic
                    alopecia
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-225">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-226" />
                  <span className="text-[#9CA3AF]" data-id="element-227">
                    10-minute daily protocol — hands-free
                  </span>
                </li>
              </ul>
            </div>

            {/* The Protocol Guide */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-8" data-id="element-228">
              <div className="flex items-center gap-3 mb-5" data-id="element-229">
                <div className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center" data-id="element-230">
                  <BookOpenIcon className="w-5 h-5 text-[#C8A04A]" data-id="element-231" />
                </div>
                <h4 className="text-lg font-bold text-[#F5F5F0]" data-id="element-232">
                  The Protocol Guide
                </h4>
              </div>
              <ul className="space-y-3" data-id="element-233">
                <li className="flex items-start gap-3" data-id="element-234">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-235" />
                  <span className="text-[#9CA3AF]" data-id="element-236">
                    Month-by-month progress benchmarks — so you know what to
                    expect and when
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-237">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-238" />
                  <span className="text-[#9CA3AF]" data-id="element-239">
                    Scalp health optimization guide — adjunct practices that
                    support the protocol
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-240">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-241" />
                  <span className="text-[#9CA3AF]" data-id="element-242">
                    Baseline tracking framework — document and measure real
                    change
                  </span>
                </li>
              </ul>
            </div>

            {/* Direct Support */}
            <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-lg p-8" data-id="element-243">
              <div className="flex items-center gap-3 mb-5" data-id="element-244">
                <div className="w-10 h-10 rounded-full bg-[#2A2A2A] flex items-center justify-center" data-id="element-245">
                  <HeadphonesIcon className="w-5 h-5 text-[#C8A04A]" data-id="element-246" />
                </div>
                <h4 className="text-lg font-bold text-[#F5F5F0]" data-id="element-247">
                  Direct Support
                </h4>
              </div>
              <ul className="space-y-3" data-id="element-248">
                <li className="flex items-start gap-3" data-id="element-249">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-250" />
                  <span className="text-[#9CA3AF]" data-id="element-251">
                    Access to the CrownControl™ support team — real humans, not
                    bots
                  </span>
                </li>
                <li className="flex items-start gap-3" data-id="element-252">
                  <CheckCircleIcon className="w-5 h-5 text-[#C8A04A] mt-0.5 flex-shrink-0" data-id="element-253" />
                  <span className="text-[#9CA3AF]" data-id="element-254">
                    Science Q&A resource — straight answers about the research
                  </span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Investment + CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mt-20 text-center" data-id="element-255">
          <h3 className="text-2xl md:text-3xl font-bold text-[#F5F5F0] tracking-tight mb-6" data-id="element-256">
            The Investment
          </h3>
          <p className="text-lg text-[#9CA3AF] leading-relaxed max-w-2xl mx-auto mb-4" data-id="element-257">
            <span className="text-3xl font-bold text-[#C8A04A]" data-id="element-258">$497</span> —
            one time.
          </p>
          <p className="text-lg text-[#9CA3AF] leading-relaxed max-w-2xl mx-auto mb-10" data-id="element-259">
            Less than one month of most transplant payment plans. Less than a
            year of finasteride with bloodwork. Less than the supplements most
            men cycle through before finding something with clinical backing.
          </p>
          <p className="text-lg text-[#F5F5F0] font-medium mb-8" data-id="element-260">
            A device you own. A protocol you control. A mechanism with
            documented evidence.
          </p>
          <CTAButton data-id="element-261" />
        </motion.div>
      </div>
    </section>;
}